import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import "./ResourceDetail.css";
import "../DataModel.css";
import "../../DataOfferings/DataOfferings.css";
import DeleteButton from "../../../components/buttons/DeleteButton";
import EditButton from "../../../components/buttons/EditButton";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import BreadCrumb from "../../../components/HeaderComponents/BreadCrum";
import { Spinner } from "../../../components/Filtering/Reusable/Spinner";
import ExpandableText from "../../../components/ExpandableText/ExpandableText";
import DetailItem from "../../../components/DetailItem/DetailItem";
import DetailField from "../../../components/DetailField/DetailField";
import { useWorkerApiHelper } from "../../../hooks/useWorkerApiHelper";
import { useTableControls } from "../../../hooks/useTableControls";
import {
  getTextByLang,
  formatArray,
  renderLinks,
  mimeToKey,
} from "../../../utils/formUtils";
import { renderThemes } from "../../../utils/constants";
import { showErrorAlert } from "../../../utils/showErrorAlert";
import { useGetVocabById } from "../../../hooks/vocabularies/useGetVocabById";
import { useDeleteVocab } from "../../../hooks/vocabularies/useDeleteVocab";
import { useGetParticipantsOld } from "../../../hooks/participants/useGetParticipantsOld";
import {
  buildParticipantsById,
  getParticipantLabelById,
} from "../../../utils/participantLookup";

export default function ResourceDetail() {
  const { callApiWithWorker } = useWorkerApiHelper();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { identifier } = useParams();
  const location = useLocation();

  const record = location.state?.record;

  const [dataset, setDataset] = useState(null);
  const [loading] = useState(false);
  const currentLang = (i18n.language || "en").split("-")[0].toLowerCase();

  const { orgProvider, setTableData } = useTableControls([]);
  const { mutateAsync: deleteResource, isPending: isDeleting } = useDeleteVocab({
    callApiWithWorker,
    currentLang: i18n.language,
    t,
  });
  const {
    data: fetchedVocab,
    isFetching: isFetchingVocab,
    isError: isErrorVocab,
    error: vocabError,
  } = useGetVocabById({
    callApiWithWorker,
    id: identifier,
    currentLang: i18n.language,
    enabled: Boolean(identifier),
  });
  const { data: participantsResponse } = useGetParticipantsOld({
    callApiWithWorker,
    lang: currentLang,
    pageIndex: 1,
    pageSize: 1000,
  });

  const parseWorkerPayload = (res) => {
    if (typeof res?.body === "string") {
      try {
        return JSON.parse(res.body);
      } catch {
        return res?.data ?? res;
      }
    }

    return res?.data ?? res;
  };

  const normalizeResourceForDetail = useCallback(
    (raw) => {
      const metadata = raw?.metadata ?? {};

      return {
        ...raw,
        name:
          getTextByLang(raw?.title, i18n.language) ||
          getTextByLang(metadata?.title, i18n.language) ||
          getTextByLang(raw?.name, i18n.language),
        theme: raw?.theme ?? metadata?.theme ?? [],
        author: raw?.author ?? metadata?.author ?? [],
        extension:
          raw?.resource_extension ??
          raw?.extension ??
          metadata?.extension ??
          raw?.resource_file_extension,
        version:
          raw?.version_info ??
          raw?.version ??
          metadata?.version_info ??
          metadata?.version,
        status: raw?.status ?? metadata?.status ?? "",
        license: raw?.license ?? metadata?.license ?? "",
        description:
          getTextByLang(metadata?.description, i18n.language) ||
          getTextByLang(raw?.description, i18n.language) ||
          "",
        versionNotes:
          getTextByLang(metadata?.version_notes, i18n.language) ||
          getTextByLang(raw?.version_notes, i18n.language) ||
          "",
        type: raw?.type ?? metadata?.type ?? "",
        publisher: metadata?.publisher ?? raw?.publisher ?? "",
        language: metadata?.language ?? raw?.language ?? [],
        documentation: metadata?.documentation ?? raw?.documentation ?? [],
        geographicalCoverage:
          metadata?.spatial ?? raw?.spatial ?? raw?.geographicalCoverage ?? [],
        frequency: metadata?.frequency ?? raw?.frequency ?? "",
        modified: raw?.modified ?? metadata?.modified ?? "",
      };
    },
    [i18n.language],
  );

  useEffect(() => {
    if (!fetchedVocab) {
      setDataset(null);
      setTableData([]);
      return;
    }

    const parsed = parseWorkerPayload(fetchedVocab);
    const normalized = normalizeResourceForDetail(parsed);
    setDataset(normalized);
    setTableData([normalized]);
  }, [fetchedVocab, normalizeResourceForDetail, setTableData]);

  useEffect(() => {
    if (!isErrorVocab) return;
    showErrorAlert(vocabError);
  }, [isErrorVocab, vocabError]);

  const participantsById = useMemo(
    () => buildParticipantsById(participantsResponse?.content),
    [participantsResponse?.content],
  );

  const publisherLabel = useMemo(() => {
    return getParticipantLabelById(
      dataset?.publisher,
      participantsById,
      currentLang,
    );
  }, [currentLang, dataset?.publisher, participantsById]);

  const handleDelete = async (identifier) => {
    try {
      if (!identifier) {
        throw new Error("Missing fileHash/identifier for delete.");
      }

      const result = await Swal.fire({
        title: t("dataModels.deleteMessage"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: t("dataModels.deleteMessageYes"),
        cancelButtonText: t("dataModels.deleteMessageNo"),
      });

      if (!result.isConfirmed) return;

      await deleteResource(identifier);
      setDataset(null);
      setTableData([]);
    } catch (error) {
      console.error("Error deleting resource:", error);
    }
  };

  if ((isFetchingVocab || loading || isDeleting) && !dataset) {
    return (
      <div className="spinner-container">
        <Spinner size="large" color="var(--main-color)" />
      </div>
    );
  }

  if (!dataset) {
    return (
      <section className="pb4 resource-detail-page">
        <div className="w100 flexbread">
          <div className="divbreadcrum w90">
            <BreadCrumb />
          </div>
        </div>
        <div className="container containerflex">
          <div className="w90 bgwhite p2">{t("datatable.noData")}</div>
        </div>
      </section>
    );
  }

  return (
    <section className="pb4 resource-detail-page">
      <div className="w100 flexbread">
        <div className="divbreadcrum w90">
          <BreadCrumb />
        </div>
      </div>
      <div className="container containerflex resource-detail-shell">
        <div className="resourcetitlediv w90 resource-detail-header">
          <div className="left-block">
            <button
              onClick={() => navigate("/datamodel")}
              className="caret-left-button-1"
            >
              <ArrowBackIosIcon />
            </button>
            <h2 className="tittlecatalog">{getTextByLang(dataset?.name)}</h2>
          </div>
          {orgProvider && (
            <div className="flexdivbotonesdetailpage resource-detail-actions">
              <div className="plr6">
                <DeleteButton onClick={() => handleDelete(identifier)} />
              </div>

              <div>
                <EditButton
                  onClick={() =>
                    navigate(`/editresource/${identifier}`, {
                      state: { record, dataset },
                    })
                  }
                />
              </div>
            </div>
          )}
        </div>
        <div className="divgris w90 resource-detail-kpis">
          <DetailItem title={t("dataModelDetail.detail.theme")}>
            {renderThemes(dataset?.theme)}
          </DetailItem>
          <DetailItem title={t("dataModelDetail.detail.author")}>
            {formatArray(dataset?.author)}
          </DetailItem>
          <DetailItem title={t("dataModelDetail.detail.format")}>
            {mimeToKey(dataset.extension)}
          </DetailItem>
          <DetailItem title={t("dataModelDetail.detail.version")}>
            <span className="licensedata">{dataset.version}</span>
          </DetailItem>
          <DetailItem title={t("dataModelDetail.detail.status")}>
            {dataset.status}
          </DetailItem>
          <DetailItem title={t("dataModelDetail.detail.license")}>
            <span>{renderLinks(dataset.license)}</span>
          </DetailItem>
        </div>
      </div>
      <div className="containerflexstart container mb2 resource-detail-main">
        <div className="w90 bgwhite resource-detail-content-card">
          <div className="p1">
            <h3 className="detailstitle">
              {t("dataOfferingsDetail.detailsSection")}
            </h3>
          </div>

          <div className="divdescriptionOffer plr2 resource-detail-description-wrap">
            <div className="left-column">
              <div className="bordergris">
                <h4 className="mtb descriptiontitle">
                  {t("dataModelDetail.detail.description")}
                </h4>
                <ExpandableText text={dataset?.description} maxLines={3} />
              </div>
              <div className="bordergris">
                <h4 className="mtb descriptiontitle">
                  {t("dataModelDetail.detail.version_notes")}
                </h4>
                <ExpandableText text={dataset?.versionNotes} maxLines={3} />
              </div>
            </div>
          </div>
          <div className="divvocabulary plr2 resource-detail-fields-grid">
            <DetailField
              label={t("dataModelDetail.detail.type")}
              value={dataset.type}
            />
            <DetailField
              label={t("dataModelDetail.detail.publisher")}
              value={publisherLabel}
            />
            <DetailField
              label={t("dataModelDetail.detail.language")}
              value={formatArray(dataset.language)}
            />
            <DetailField
              label={t("dataModelDetail.detail.documentationUrl")}
              value={renderLinks(dataset.documentation)}
            />
            <DetailField
              label={t("dataModelDetail.detail.country")}
              value={formatArray(dataset.geographicalCoverage)}
            />
            <DetailField
              label={t("dataModelDetail.detail.frequency")}
              value={dataset.frequency || "N/A"}
            />
            <DetailField
              label={t("dataModelDetail.detail.lastModified")}
              value={dataset.modified}
            />
          </div>
        </div>
      </div>
      {loading && (
        <div className="loading">
          <Spinner size="large" color="var(--main-color)" />
        </div>
      )}
    </section>
  );
}
